import React, { useEffect, useState } from "react";
import ClientFormField from "src/components/kyxClientForm/ClientFormField";
import { applicationStore } from "src/store/ApplicationStore";
import { observer } from "mobx-react";
import { useForm } from "react-hook-form";
import LoadingComponent from "src/components/ui/LoadingComponent";
import Preloader from "src/components/ui/Preloader";
import { toast } from "react-toastify";
import { Button } from "src/components/ui/Button";
import logger from "src/utils/logger";
import ConditionEvaluatorFactory from "src/evaluators/ConditionEvaluatorFactory";
import { filterFormData, filterRawData, filterValidUbos, processCompanyFormData, processEddFormData, processFormData } from "src/helpers/formUtils";
import { toJS } from "mobx";
import PreviewForm from "./components/PreviewForm";
import { useTranslation } from "react-i18next";
import { processRawDataLabels } from "src/helpers/multilingualHelper";


const MAX_TOTAL_FILE_SIZE_MB = 10;

const KyxClientForm = observer(() => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || 'en';
  const { control, setValue, register, handleSubmit, watch, formState: { errors } } = useForm();
  const [formData, setFormData] = useState([]);
  const [formName, setFormName] = useState('');
  const [clientFormRules, setClientFormRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const evaluator = ConditionEvaluatorFactory.createEvaluator('clientForm');

  const getClientForm = async () => {
    try {
      setLoading(true);
      const response = applicationStore.isEddForm === true ? applicationStore.eddServiceResponse.clintForm : applicationStore.isPeriodicForm === true ? applicationStore.periodicServiceResponse.clientForm : applicationStore.workflowNextServiceResponse?.response_context
      const form = toJS(response.form);
      logger.log(form)
      const keyIndividualsSection = form?.find(
        item => item?.config?.component === "Key Individuals and Entities" && Array.isArray(item.fields)
      );
      if (keyIndividualsSection) {
        applicationStore.setClientFormOriginalFieldCount(keyIndividualsSection.fields.length);
      }
      const formWithUboPrefixes = Object.values(form).map((element) => {
        if (element.config && element.config.component === "Key Individuals and Entities") {
          element.fields = element.fields.map(field => {
            field.config.uboId = "ubo_1";
            field.required = true
            return field;
          });
        }
        return element;
      });

      const updatedForm = applyInitialHiddenFields(formWithUboPrefixes);

      setFormData(updatedForm);
      setFormName(response.name);
      setClientFormRules(response.rules);

      setLoading(false);
    } catch (error) {
      logger.log(error);
      setLoading(false);
    }
  };

  const applyInitialHiddenFields = (form) => {
    return form.map((element) => {
      if (element.config?.type === "section" && Array.isArray(element.fields) && element.fields.length > 0) {
        const updatedFields = element.fields.map(field => {
          if (field.config?.isHidden) {
            return {
              ...field,
              hidden: true
            };
          }
          return field;
        });

        return {
          ...element,
          fields: updatedFields
        };
      }

      if (element.config?.isHidden) {
        return {
          ...element,
          hidden: true
        };
      }

      return element;
    });
  };

  const handleChange = (elementId, event, elementValue) => {
    const updatedForm = formData.map(item => {
      const updatedItem = { ...item }
      if (updatedItem.id === elementId) {
        updatedItem.value = event ? event.target.value : elementValue
        return updatedItem
      }
      if (updatedItem.config.type === "section") {
        updatedItem.fields.forEach(field => {
          if (field.id === elementId) {
            field.value = event ? event.target?.value : elementValue
          }
        });
        if (updatedItem.config.rules && updatedItem.config.rules.length > 0) {
          applyClientFormRules(elementId, [...updatedItem.fields], updatedItem.config.rules);
        }
      }
      return updatedItem;
    });
    applyClientFormRules(elementId, updatedForm, clientFormRules);
    setFormData(updatedForm);
    logger.log('New Form DATA', updatedForm);
  };

  const populateFormData = (formData, data) => {
    const currentForm = [...formData];

    Object.keys(data).forEach(elementId => {
      const elementValue = data[elementId];
      const changedElementIndex = currentForm.findIndex(item => item.id === elementId);

      if (changedElementIndex === -1) {
        currentForm.forEach((item, index) => {
          if (item.config.type === "section" && !item.hidden) {
            const foundFieldIndex = item.fields.findIndex(field => field.id === elementId);
            if (foundFieldIndex > -1) {
              const changedSectionElementIndex = currentForm.findIndex(i => i.id === item.id);

              if (currentForm[changedSectionElementIndex]?.fields[foundFieldIndex]?.hidden) return
              currentForm[changedSectionElementIndex].fields[foundFieldIndex].value = elementValue;
              return;
            }
          }
        });
      } else {
        if (currentForm[changedElementIndex]?.hidden) return
        currentForm[changedElementIndex].value = elementValue;
      }
    });

    logger.log('Populated Form DATA', currentForm);
    return currentForm;
  };

  const applyClientFormRules = (elementId, updatedForm, formRules) => {
    formRules.forEach(rule => {
      const isElementInRule = rule.conditions.some(condition => condition.field.id === elementId);
      if (isElementInRule) {
        const conditionsMet = evaluator.evaluateConditions(rule.conditions, updatedForm);
        rule.actions.forEach(action => {
          let targetIndex = updatedForm.findIndex(item => item.id === action.target.id);

          if (targetIndex === -1) {
            targetIndex = updatedForm.findIndex(item =>
              item.config.type === "section" &&
              item.fields.some(subItem => subItem.id === action.target.id)
            );

            if (targetIndex !== -1) {
              const sectionFieldIndex = updatedForm[targetIndex].fields.findIndex(subItem => subItem.id === action.target.id);
              if (sectionFieldIndex !== -1) {
                switch (action.action) {
                  case "hide":
                    updatedForm[targetIndex].fields[sectionFieldIndex].hidden = conditionsMet;
                    updatedForm[targetIndex].fields[sectionFieldIndex].value = null;
                    break;
                  case "show":
                    updatedForm[targetIndex].hidden = !conditionsMet;
                    updatedForm[targetIndex].fields[sectionFieldIndex].hidden = !conditionsMet;
                    break;
                  default:
                    break;
                }
              }
              return;
            }
          }

          if (targetIndex !== -1) {
            switch (action.action) {
              case "hide":
                updatedForm[targetIndex].hidden = conditionsMet;
                updatedForm[targetIndex].value = null;
                break;
              case "show":
                updatedForm[targetIndex].hidden = !conditionsMet;
                break;
              default:
                break;
            }
          }
        });
      }
    });
  };

  useEffect(() => {
    getClientForm();
  }, []);

  const handleAddFields = (fieldId, newSection) => {
    const updatedFormData = formData.map(field => {
      if (field.id === fieldId) {
        return {
          ...field,
          fields: [...(field.fields || []), ...newSection.fields],
          config: {
            ...field.config,
            rules: [...(field.config?.rules || []), ...(newSection.config?.rules || [])]
          }
        };
      }
      return field;
    });

    setFormData(updatedFormData);
    logger.log("Updated Form Data (with new rules):", updatedFormData);
    return true
  };

  const handleRemoveFields = (fieldId, uboId) => {
    let updatedFields = [];
    let removedFieldIds = [];

    const updatedFormData = formData.map(field => {
      if (field.id === fieldId) {
        const fieldsToRemove = field.fields.filter(subField => subField.config.uboId === uboId);
        removedFieldIds = fieldsToRemove.map(f => f.id);

        updatedFields = field.fields.filter(subField => subField.config.uboId !== uboId);

        const updatedRules = (field.config?.rules || []).filter(rule => {
          const allConditionIds = rule.conditions.map(c => c.field.id);
          const allActionIds = rule.actions.map(a => a.target.id);

          const usesRemovedFields = [...allConditionIds, ...allActionIds].some(id => removedFieldIds.includes(id));
          return !usesRemovedFields;
        });

        return {
          ...field,
          fields: updatedFields,
          config: {
            ...field.config,
            rules: updatedRules
          }
        };
      }
      return field;
    });

    setFormData(updatedFormData);
    logger.log("Form updated after UBO removal:", updatedFormData);

    return updatedFields;
  };
  const reorderCountryField = (element) => {
    const fields = element?.fields;
    if (!fields || element?.config?.component !== 'Address' || !Array.isArray(fields)) return fields;
    const index = fields.findIndex(
      (field) => field?.config?.dbKey === 'address_country' || field?.config?.dbKey === 'registered_country'
    );
    if (index === -1) return fields;
    const reordered = [fields[index], ...fields.slice(0, index), ...fields.slice(index + 1)];
    return reordered;
  };

  const handleBackToForm = () => {
    if (previewData) {
      setFormData(populateFormData(formData, previewData));
    }
    setPreviewMode(false);
  };

  const onSubmit = async data => {
    const updatedFormData = populateFormData(formData, data);
    logger.log(updatedFormData)
    const totalBase64SizeMB = updatedFormData.reduce((acc, item) => {
      if (item.config?.type === "section" && Array.isArray(item.fields)) {
        item.fields.forEach(field => {
          if (field.config?.type === "file" && typeof field.value === "string") {
            const base64Length = field.value.length - (field.value.indexOf(",") + 1);
            const sizeInBytes = 4 * Math.ceil(base64Length / 3) * 0.5624896334383812;
            acc += sizeInBytes;
          }
        });
      } else if (item.config?.type === "file" && typeof item.value === "string") {
        const base64Length = item.value.length - (item.value.indexOf(",") + 1);
        const sizeInBytes = 4 * Math.ceil(base64Length / 3) * 0.5624896334383812;
        acc += sizeInBytes;
      }
      return acc;
    }, 0);

    const totalMB = parseFloat((totalBase64SizeMB / (1024 * 1024)).toFixed(2));
    logger.log("Total base64 file size in MB:", totalMB);
    if (totalMB > MAX_TOTAL_FILE_SIZE_MB) {
      toast.warning(t('clientForm.totalFileSizeExceeded', { size: MAX_TOTAL_FILE_SIZE_MB }));
      return;
    }
    try {
      setSubmitting(true);

      const formObject = applicationStore.type === "company" ?
        processCompanyFormData(updatedFormData) : applicationStore.type === "EDD" || applicationStore.type === "PERIODIC" ?
          processEddFormData(updatedFormData) :
          processFormData(updatedFormData);
      const processedRawData = processRawDataLabels(formObject.raw_data, currentLanguage);
      const raw_data = filterRawData(processedRawData);
      const form_data = filterFormData(formObject.form_data);
      const ubos = formObject.ubos || [];
      const cleanedUbos = filterValidUbos(ubos);
      const filteredUbos = cleanedUbos.length > 0 ? cleanedUbos : undefined;
      if (!form_data["Country"] && applicationStore.type !== "EDD" && applicationStore.type !== "PERIODIC") {
        form_data["Country"] = applicationStore.conditionContext?.country;
      }
      const previewForm = applicationStore.type === "company" || applicationStore.type === "EDD" || applicationStore.type === "PERIODIC" ? {
        ...form_data,
        raw_data,
        ...(filteredUbos ? { "ubos": filteredUbos } : {})
      } : {
        ...form_data,
        raw_data
      }
      logger.log(previewForm)
      setPreviewData(previewForm);
      setPreviewMode(true);

      logger.log(applicationStore.conditionContext);
      //logger.log(response.data);

    } catch (error) {
      toast.error(error);
      logger.log(error);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <>
      {loading ? (
        <LoadingComponent />
      ) :
        (<div className="flex h-full min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden">
          {previewMode && (
            <div className="form-preview-container">
              <PreviewForm formData={previewData} handleBackToForm={handleBackToForm} />
            </div>
          )}

          <form
            onSubmit={handleSubmit(onSubmit)}
            className={`flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden ${previewMode ? "hidden" : ""}`}
          >
            <div className="form-edit-scroll flex min-h-0 flex-1 flex-col items-start gap-6 overflow-y-auto overscroll-y-contain px-5 pt-8 touch-pan-y">
              <div className="mb-4 w-full">
                <h1 className="text-h5 font-bold text-flow-heading w-8/12">{formName}</h1>
              </div>

              {formData.map((element) => (
                !element.hidden && (
                  <ClientFormField
                    setValue={setValue}
                    element={element}
                    key={element.id}
                    id={element.id}
                    type={element.config.type}
                    options={element.config.options}
                    name={element.name}
                    config={element.config}
                    {...element}

                    fields={reorderCountryField(element)}
                    register={register}
                    errors={errors}
                    control={control}
                    watch={watch}
                    onChange={handleChange}
                    onAddFields={handleAddFields}
                    onRemoveFields={handleRemoveFields}
                    value={element.value}
                  />
                )
              ))}
            </div>

            <div className="flow-form-action-footer shrink-0 w-full border-t border-black/[0.08] px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))]">
              <Button
                type="submit"
                className="w-full"
                variant="primaryMediumWithoutIcon"
                disabled={submitting}
              >
                <span className="me-1">{t("clientForm.preview")}</span>
                {submitting && <Preloader variant="white" />}
              </Button>
            </div>
          </form>
        </div>
        )}
    </>
  );
});

export default KyxClientForm;
